<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import SearchBox from '@/components/SearchBox.vue'
import UiGroup from '@/components/UiGroup.vue'
import UiList from '@/components/UiList.vue'
import TagNameInput from '@/components/todos/TagNameInput.vue'
import { NEW_TAG, useTagEditor } from '@/composables/useTagEditor'
import { tagsPath } from '@/settings/tagsNav'
import { redundantImplications } from '@/settings/tagTips'
import { searchTags } from '@/todos/tagSearch'
import { chipStyle } from '@/todos/tags'
import UiRow from '@/components/UiRow.vue'

// The first level of the tag settings, like the Settings root: a search over the tags and a row per tag that opens the
// tag. `?q=` keeps the search in the URL (replaced, so it makes no history entries); adding a tag is a row at the end.
const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const editor = useTagEditor()
const { sorted, drafts, marked, candidates, todosLoading, todosRequested } = editor

const query = ref(typeof route.query.q === 'string' ? route.query.q : '')
const tipCount = computed(() => redundantImplications(editor.tags.value).length)
const shown = () => searchTags(sorted.value, query.value)

watch(query, (q) => {
  const rest = { ...route.query }
  delete rest.q
  void router.replace({ query: q ? { ...rest, q } : rest })
})

const adding = ref(false)
const input = ref<InstanceType<typeof TagNameInput> | null>(null)

async function startAdd(): Promise<void> {
  adding.value = true
  drafts[NEW_TAG] ??= ''
  await nextTick()
  input.value?.focus()
}

function stopAdd(): void {
  adding.value = false // (the draft stays)
}

function cancelAdd(): void {
  editor.cancel(NEW_TAG)
  adding.value = false
}

async function accept(): Promise<void> {
  await editor.create((tag) => void editor.scrollToTag(tag.id))
  if (drafts[NEW_TAG] === undefined) adding.value = false
}

async function pickNew(name: string): Promise<void> {
  drafts[NEW_TAG] = name
  await accept()
}

// The add row stays open while the focus only moves to another control of the row (the cancel and save buttons).
function onAddBlur(event: FocusEvent): void {
  const row = (event.currentTarget as HTMLElement).closest('.ui-row')
  if (event.relatedTarget instanceof Node && row?.contains(event.relatedTarget)) return
  stopAdd()
}
</script>

<template>
  <div class="tag-list">
    <p v-if="editor.error.value" class="error">{{ editor.error.value }}</p>
    <p v-if="editor.loading.value && !editor.loaded.value">{{ t('common.loading') }}</p>

    <UiGroup v-if="tipCount">
      <UiList>
        <UiRow :to="tagsPath('tips')" class="tips" data-testid="tips-row">
          <span class="tile" aria-hidden="true">💡</span>
          <span class="tips-text">
            <span>{{ t('settings.tags.tips.title') }}</span>
            <span class="ui-hint">{{ t('settings.tags.tips.hint') }}</span>
          </span>
          <span class="count" data-testid="tips-badge" :aria-label="t('settings.tags.tips.badge', tipCount)">{{
            tipCount
          }}</span>
          <span class="go">›</span>
        </UiRow>
      </UiList>
    </UiGroup>

    <UiGroup>
      <SearchBox
        v-model="query"
        :placeholder="t('list.search')"
        :clear-label="t('list.clearSearch')"
      />
      <template #footer>
        <p v-if="editor.loaded.value && !sorted.length && !adding">
          {{ t('settings.tags.none') }}
        </p>
        <p v-else-if="query && !shown().length">{{ t('settings.tags.noMatches') }}</p>
      </template>
    </UiGroup>

    <UiGroup v-if="shown().length">
      <UiList>
        <UiRow
          v-for="tag in shown()"
          :key="tag.id"
          :to="tagsPath(tag.id)"
          class="entry"
          :data-tag-id="tag.id"
          :data-focus="marked === tag.id ? 'true' : undefined"
        >
          <span class="label" :style="chipStyle(tag.color)"># {{ tag.name }}</span>
          <span class="spacer"></span>
          <span v-if="tag.aliases.length" class="ui-hint">{{
            t('settings.tags.aliasesCount', tag.aliases.length)
          }}</span>
          <span class="go">›</span>
        </UiRow>
      </UiList>
    </UiGroup>

    <UiGroup>
      <template #footer>
        <p v-if="adding && todosLoading">{{ t('common.loading') }}</p>
        <p v-else-if="adding && todosRequested && !candidates.length">
          {{ t('settings.tags.aliasPickNone') }}
        </p>
        <p v-if="drafts[NEW_TAG] !== undefined && !adding" class="pending">
          {{ t('settings.tags.unsaved') }}
        </p>
        <p>{{ t('settings.tags.aliasHint') }}</p>
        <p>{{ t('settings.tags.impliesHint') }}</p>
      </template>
      <UiList class="suggest">
        <UiRow v-if="adding">
          <TagNameInput
            ref="input"
            v-model="drafts[NEW_TAG]"
            class="name-input"
            :names="candidates"
            :placeholder="t('settings.tags.name')"
            :aria-label="t('settings.tags.name')"
            @focus="editor.ensureTodosLoaded"
            @pick="pickNew"
            @submit="accept"
            @escape="cancelAdd"
            @blur="onAddBlur"
          />
          <button
            type="button"
            class="icon"
            :title="t('common.save')"
            @mousedown.prevent
            @click="accept"
          >
            ✓
          </button>
          <button
            type="button"
            class="icon"
            :title="t('common.cancel')"
            @mousedown.prevent
            @click="cancelAdd"
          >
            ✕
          </button>
        </UiRow>
        <UiRow as="button" v-else type="button" class="add" @click="startAdd">
          ＋ {{ t('common.add') }}
        </UiRow>
      </UiList>
    </UiGroup>
  </div>
</template>

<style scoped>
/* (the suggestion list of the add row hangs out of the card) */
.ui-list.suggest {
  overflow: visible;
}

.tips {
  color: var(--tg-text-color);
  text-decoration: none;
}

.tile {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  background: var(--tg-secondary-bg-color);
}

.tips-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.count {
  min-width: 1.4rem;
  padding: 0 0.4rem;
  border-radius: 999px;
  background: var(--tg-destructive-text-color);
  color: #fff;
  font-size: 0.8rem;
  text-align: center;
}

.entry {
  color: var(--tg-text-color);
  text-decoration: none;
}

.entry[data-focus='true'] {
  background: var(--tg-secondary-bg-color);
  box-shadow: inset 3px 0 0 var(--tg-button-color);
}

.label {
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  min-width: 0;
  overflow-wrap: anywhere;
}

.spacer {
  flex: 1;
}

.go {
  color: var(--tg-hint-color);
  font-size: 1.2rem;
}

.add {
  width: 100%;
  border: 0;
  background: none;
  color: var(--tg-link-color);
  text-align: left;
  font: inherit;
  cursor: pointer;
}

.name-input {
  flex: 1;
  min-width: 0;
}

.icon {
  width: auto;
  padding: 0.1rem 0.4rem;
  background: none;
  color: inherit;
  border: none;
  cursor: pointer;
}

.pending {
  font-style: italic;
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
